import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { profileAPI } from '../lib/api';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Save, User, Mail, Phone, Globe, Shield, Info, Lock } from 'lucide-react';

export default function UserSettingsPage() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', email: '', phone: '', bio: '', handle: '', custom_domain: '' });
  const [saving, setSaving] = useState(false);
  const [planData, setPlanData] = useState(null);

  useEffect(() => {
    if (user) {
      setForm({
        username: user.username || user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        bio: user.bio || '',
        handle: user.handle || '',
        custom_domain: user.custom_domain || '',
      });
    }
  }, [user]);

  useEffect(() => {
    api.get('/plan').then(res => setPlanData(res.data)).catch(() => {});
  }, []);

  const formatHandle = (val) => {
    return val.toLowerCase().replace(/[^a-z0-9-]/g, '').replace(/--+/g, '-');
  };

  const handleSave = async () => {
    if (!form.username.trim()) { toast.error('Username is required'); return; }
    if (!form.handle.trim()) { toast.error('Store name is required'); return; }
    setSaving(true);
    try {
      const res = await profileAPI.update({
        username: form.username.trim(),
        phone: form.phone.trim(),
        bio: form.bio.trim(),
        handle: form.handle.trim(),
      });
      updateUser(res.data);
      toast.success('Settings saved!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-[800px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: '#195259' }}>User Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your personal information</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="user-settings-save" style={{ backgroundColor: '#195259' }} className="text-white hover:opacity-90">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
          Save Changes
        </Button>
      </div>

      {/* Personal Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base" style={{ color: '#195259' }}>Personal Information</CardTitle>
          <CardDescription>Your basic account details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><User className="h-3.5 w-3.5 text-muted-foreground" /> Username</Label>
              <Input value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))} placeholder="Your display name" data-testid="user-settings-username" />
              <p className="text-[10px] text-muted-foreground">Displayed in your dashboard and notifications. Same as Profile &gt; Username.</p>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-muted-foreground" /> Email</Label>
              <Input value={form.email} disabled className="bg-muted" />
              <p className="text-[10px] text-muted-foreground">Email cannot be changed</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-muted-foreground" /> Phone Number</Label>
              <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+60 12-345 6789" data-testid="user-settings-phone" />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Globe className="h-3.5 w-3.5 text-muted-foreground" /> Store Name</Label>
              <Input value={form.handle} onChange={e => setForm(p => ({ ...p, handle: formatHandle(e.target.value) }))} placeholder="yourstore" className="font-mono" data-testid="user-settings-handle" />
              <p className="text-[10px] text-muted-foreground">Used in your store URL. Same as Profile &gt; Store Name. Lowercase, no spaces.</p>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Bio</Label>
            <Textarea value={form.bio} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))} placeholder="Tell us about yourself..." rows={3} data-testid="user-settings-bio" />
          </div>
        </CardContent>
      </Card>

      {/* Custom Domain */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4" style={{ color: '#195259' }} />
            <CardTitle className="text-base" style={{ color: '#195259' }}>Custom Domain</CardTitle>
            {(planData?.plan || user?.plan || 'free') !== 'premium' && (
              <Badge className="text-[10px] text-white" style={{ backgroundColor: '#FF9A00' }}>Premium Only</Badge>
            )}
          </div>
          <CardDescription>Connect your own domain to your Funnely store</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 p-4 rounded-xl" style={{ backgroundColor: 'rgba(25,82,89,0.06)' }}>
            <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: '#195259' }}>
              <Globe className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium" style={{ color: '#195259' }}>Coming Soon</p>
              <p className="text-xs text-muted-foreground mt-0.5">Custom domain feature is under development. Stay tuned!</p>
            </div>
            <Badge className="text-[10px] text-white shrink-0" style={{ backgroundColor: '#FF9A00' }}>Premium</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Account Security */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base" style={{ color: '#195259' }}>Account Security</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                <Shield className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium">Password</p>
                <p className="text-xs text-muted-foreground">Change your account password</p>
              </div>
            </div>
            <Button variant="outline" size="sm" disabled>Change Password</Button>
          </div>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base" style={{ color: '#195259' }}>Account Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Account ID</p>
            <p className="text-sm font-mono">{user?.id?.slice(0, 16)}...</p>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Member Since</p>
            <p className="text-sm">{user?.created_at ? new Date(user.created_at).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</p>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Account Status</p>
            <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
